<script setup lang="ts">
import { isJust, just, nothing, toMaybe, type Maybe } from "../../lib/maybe";
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  useTemplateRef,
  type Ref,
} from "vue";
import { storeToRefs } from "pinia";
import { displayFileName, documentFileName } from "../../lib/fileNames";
import { openContextMenu } from "../../lib/platform/contextMenu";
import { noteEntryDisplayName, type NoteEntry } from "../../lib/tauri/notes";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useOutlineActionsStore } from "../../stores/useOutlineActionsStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import { useCreateEntityFlow } from "../../composables/useCreateEntityFlow";
import { useRenameEntityFlow } from "../../composables/useRenameEntityFlow";
import ZwButton from "../ui/ZwButton.vue";
import ZwDialog from "../ui/ZwDialog.vue";
import ZwInput from "../ui/ZwInput.vue";

type FocusableInput = {
  focus: () => void;
  select: () => void;
};

function focusableInputRef(raw: {
  readonly value: unknown;
}): Ref<Maybe<FocusableInput>> {
  return computed(() =>
    toMaybe(raw.value as InstanceType<typeof ZwInput>),
  ) as Ref<Maybe<FocusableInput>>;
}

function isRenamingNote(name: string): boolean {
  return (
    isJust(noteRename.renaming.value) &&
    noteRename.renaming.value.value === name
  );
}

const emit = defineEmits<{ requestCloseSettings: [] }>();

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const entities = useOutlineEntitiesStore();
const outlineActions = useOutlineActionsStore();
const { activeNote } = storeToRefs(session);

function closeMobileOutline(): void {
  if (session.mobileShell) {
    session.outlineOpen = false;
  }
}

/** The name `session.activeNote` reports back once an entry is open (see
 * `open_parked_item`) — a parked chapter opens as its own `index.md`, one
 * level inside its folder, so its active-state key differs from its list name. */
function entryActiveKey(entry: NoteEntry): string {
  return entry.parked === "chapter" ? `${entry.name}/index.zwdoc` : entry.name;
}

function isEntryActive(entry: NoteEntry): boolean {
  return (
    isJust(activeNote.value) && activeNote.value.value === entryActiveKey(entry)
  );
}

function onSelectEntry(entry: NoteEntry): void {
  noteRename.cancel();
  if (typeof entry.parked !== "undefined") {
    void workspace.onSelectParkedItem(entryActiveKey(entry));
  } else {
    void workspace.onSelectProjectNote(entry.name);
  }
  closeMobileOutline();
}

const existingNoteNames = computed(() => entities.notes.map((e) => e.name));
const noteInput = useTemplateRef<InstanceType<typeof ZwInput>>("noteInput");
const noteFlow = useCreateEntityFlow({
  inputRef: focusableInputRef(noteInput),
  existingNames: existingNoteNames,
  normalizeName: (raw) => documentFileName(raw),
  emptyNameError: "Enter a note name.",
  duplicateError: "That note already exists.",
  onCreate: (finalName) => {
    void workspace.onNewProjectNote(finalName);
    void entities.refreshNotes();
  },
});

function onNewNote(): void {
  emit("requestCloseSettings");
  noteFlow.start();
}

async function onNoteContextMenu(
  event: MouseEvent,
  entry: NoteEntry,
): Promise<void> {
  const name = entry.name;
  emit("requestCloseSettings");
  noteFlow.cancel();

  await openContextMenu(event, [
    {
      id: `rename-note-${name}`,
      label: "Rename",
      action: () => {
        void noteRename.start(name, displayFileName(name));
      },
    },
    {
      id: `delete-note-${name}`,
      label: "Delete",
      action: () => {
        void (async () => {
          if (typeof entry.parked !== "undefined") {
            await workspace.onDeleteParkedItem(name, entry.parked);
          } else {
            await workspace.onDeleteProjectNote(name);
          }
          await entities.refreshNotes();
        })();
      },
    },
  ]);
}

const renameNoteInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("renameNoteInput");
const noteRename = useRenameEntityFlow<string>(
  focusableInputRef(renameNoteInput),
  {
    allowEmpty: false,
    onRename: (from, to) => {
      const entry = toMaybe(entities.notes.find((e) => e.name === from));
      return isJust(entry) && typeof entry.value.parked !== "undefined"
        ? workspace.onRenameParkedItem(from, to)
        : workspace.onRenameProjectNote(from, to);
    },
    afterCommit: just(() => {
      void entities.refreshNotes();
    }),
  },
);

function registerOutlineChrome(): void {
  outlineActions.headerNew = just({
    label: "New note",
    run: onNewNote,
    items: nothing,
  });
  outlineActions.cancelCreating = just(() => noteFlow.cancel());
}

function clearOutlineChrome(): void {
  outlineActions.headerNew = nothing;
  outlineActions.cancelCreating = nothing;
}

onMounted(registerOutlineChrome);
onActivated(registerOutlineChrome);
onDeactivated(clearOutlineChrome);
onUnmounted(clearOutlineChrome);
</script>

<template>
  <nav class="zw-outline-nav zw-scroll" aria-label="Notes">
    <div v-if="entities.notes.length === 0" class="zw-outline-empty">
      <p>Scratch notes stay out of the book export.</p>
      <ZwButton size="sm" variant="primary" @click="onNewNote">
        New note
      </ZwButton>
    </div>
    <ul v-else class="sidecar-list">
      <li
        v-for="entry in entities.notes"
        :key="entry.name"
        class="sidecar-item"
        :class="{ 'sidecar-item-active': isEntryActive(entry) }"
        @contextmenu="onNoteContextMenu($event, entry)"
      >
        <ZwInput
          v-if="isRenamingNote(entry.name)"
          :id="nothing"
          ref="renameNoteInput"
          v-model="noteRename.draft.value"
          class="file-rename"
          aria-label="Rename note"
          :label="nothing"
          @keydown.enter.prevent="noteRename.commit"
          @keydown.escape.prevent="noteRename.cancel"
          @blur="noteRename.commit"
        />
        <button
          v-else
          type="button"
          class="sidecar-link"
          :title="noteEntryDisplayName(entry)"
          @click="onSelectEntry(entry)"
          @dblclick.stop.prevent="
            noteRename.start(entry.name, displayFileName(entry.name))
          "
        >
          <span class="sidecar-link-name">{{
            noteEntryDisplayName(entry)
          }}</span>
          <span
            v-if="typeof entry.parked !== 'undefined' && entry.synopsis !== ''"
            class="sidecar-link-synopsis"
          >
            {{ entry.synopsis }}
          </span>
        </button>
      </li>
    </ul>
  </nav>

  <ZwDialog
    :open="noteFlow.open.value"
    title="New note"
    :description="nothing"
    @close="noteFlow.cancel"
  >
    <ZwInput
      :id="nothing"
      ref="noteInput"
      v-model="noteFlow.draft.value"
      :label="just('Note name')"
      placeholder="Note name"
      @keydown.enter.prevent="noteFlow.commit"
    />
    <p v-if="noteFlow.error.value.length > 0" class="zw-dialog-error">
      {{ noteFlow.error.value }}
    </p>
    <template #footer>
      <ZwButton @click="noteFlow.cancel">Cancel</ZwButton>
      <ZwButton variant="primary" @click="noteFlow.commit">Create</ZwButton>
    </template>
  </ZwDialog>
</template>

<style scoped>
.zw-outline-nav {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 var(--space-sm) var(--space-md);
}

.zw-outline-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-sm);
  margin: var(--space-sm) var(--space-sm) 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-outline-empty p {
  margin: 0;
}

.sidecar-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.sidecar-item {
  border-radius: 0.25rem;
}

.sidecar-item-active {
  background: color-mix(in oklch, var(--color-zw-amber) 20%, transparent);
}

.sidecar-link {
  appearance: none;
  border: 0;
  background: transparent;
  display: block;
  width: 100%;
  min-width: 0;
  padding: 0.28rem 0.45rem;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border-radius: 0.25rem;
}

.sidecar-link-name {
  display: block;
  font-size: 0.92rem;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidecar-link-synopsis {
  display: block;
  margin-top: 0.1rem;
  font-size: 0.76rem;
  line-height: 1.3;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidecar-link:hover {
  background: var(--hover-fill);
}

.sidecar-link:hover .sidecar-link-name {
  color: var(--text-strong);
}

.sidecar-item-active .sidecar-link .sidecar-link-name {
  color: var(--text-strong);
}

.sidecar-item :deep(input.file-rename) {
  flex: 1;
  min-width: 0;
  width: auto;
  margin: 0.15rem 0.2rem;
  padding: 0.2rem 0.35rem;
  font-size: 0.92rem;
}
</style>
